import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import TrustBar from '../../../components/feature/TrustBar';
import ProcessSection from '../../../components/feature/ProcessSection';
import WhyChooseUs from '../../../components/feature/WhyChooseUs';
import ServiceAreasSection from '../../../components/feature/ServiceAreasSection';
import TestimonialsSection from '../../../components/feature/TestimonialsSection';
import FAQSection from '../../../components/feature/FAQSection';
import FinalCTASection from '../../../components/feature/FinalCTASection';
import { houstonFaqs } from '../../../mocks/faq';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const LOCAL_SERVICES = [
  { icon: 'ri-building-line', name: 'Commercial Cleaning', slug: '/services/commercial-cleaning', desc: 'Full-facility commercial cleaning for Houston businesses' },
  { icon: 'ri-brush-line', name: 'Janitorial Services', slug: '/services/janitorial', desc: 'Daily & recurring janitorial maintenance programs' },
  { icon: 'ri-computer-line', name: 'Office Cleaning', slug: '/services/office-cleaning', desc: 'Professional office & workspace cleaning solutions' },
  { icon: 'ri-layout-bottom-line', name: 'Floor Care & Maintenance', slug: '/services/floor-care', desc: 'Stripping, waxing, buffing & floor restoration' },
  { icon: 'ri-building-2-line', name: 'Post-Construction Cleaning', slug: '/services/post-construction', desc: 'Post-build deep cleaning for Houston construction projects' },
  { icon: 'ri-sparkling-line', name: 'Specialized Cleaning', slug: '/services/specialized', desc: 'Custom solutions for medical, hospitality & industrial spaces' },
];

/* ─── 1. Hero ─── */
function HoustonHero() {
  const { ref, isVisible } = useScrollAnimation();
  return (
    <section className="relative min-h-[75vh] flex items-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/93aa33b1dda70283c7923426af658eea.jpg"
          alt="Commercial cleaning services Houston TX"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#03143A]/95 via-[#03143A]/80 to-[#03143A]/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#03143A]/50 via-transparent to-[#03143A]/20" />
      </div>
      <div className="absolute left-0 inset-y-0 w-1 bg-gradient-to-b from-transparent via-[#C70532] to-transparent opacity-60" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-36 pb-24 w-full">
        <div ref={ref} className={`max-w-3xl transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <nav className="flex items-center gap-2 text-white/50 text-sm mb-6">
            <Link to="/" className="hover:text-white transition-colors cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-white font-medium">Houston, TX</span>
          </nav>
          <div className="inline-flex items-center gap-2 px-5 py-2 bg-[#C70532]/20 border border-[#C70532]/30 text-[#C70532] text-xs font-bold uppercase tracking-widest rounded-full mb-6">
            <i className="ri-map-pin-fill" />
            Houston, Texas
          </div>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-white mb-6 leading-tight">
            Commercial Cleaning Services in Houston, TX
          </h1>
          <p className="text-xl text-white/70 max-w-2xl leading-relaxed mb-10">
            Prime Facility Services Group delivers professional commercial cleaning and facility services to businesses throughout <strong className="text-white">Houston and the Greater Houston area</strong> — consistent, reliable, and results-driven.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/contact" className="px-8 py-4 bg-[#C70532] hover:bg-[#a80428] text-white font-bold rounded-full transition-all cursor-pointer whitespace-nowrap">
              Get a Free Quote in Houston
            </Link>
            <a href="tel:+17133382553" className="flex items-center gap-2 px-8 py-4 bg-white/10 border-2 border-white/25 hover:border-white text-white font-bold rounded-full transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-phone-fill text-[#C70532]" />
              (713) 338-2553
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── 3. Local Services (Services Overview equivalent) ─── */
function LocalServices() {
  const { ref, isVisible } = useScrollAnimation();
  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div ref={ref} className={`text-center mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="inline-block px-5 py-2 bg-[#C70532]/10 text-[#C70532] text-xs font-bold uppercase tracking-widest rounded-full mb-5">Services in Houston</span>
          <h2 className="text-4xl md:text-5xl font-black text-[#03143A] mb-5 leading-tight">Services Available in Houston, TX</h2>
          <p className="text-lg text-gray-500 max-w-2xl mx-auto leading-relaxed">Every service we offer is available to businesses throughout Houston and the surrounding Greater Houston area — all delivered by our local team.</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-14">
          {LOCAL_SERVICES.map((s, i) => (
            <Link
              key={s.slug}
              to={s.slug}
              className={`flex items-start gap-5 p-7 bg-[#FAF8F5] hover:bg-[#03143A] rounded-2xl border border-gray-100 hover:border-[#03143A] transition-all duration-300 group cursor-pointer ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${i * 70}ms` }}
            >
              <div className="w-13 h-13 flex items-center justify-center bg-[#03143A] group-hover:bg-[#C70532] rounded-xl flex-shrink-0 transition-colors duration-300">
                <i className={`${s.icon} text-white text-xl`} />
              </div>
              <div className="flex-1">
                <h4 className="font-bold text-[#03143A] group-hover:text-white transition-colors mb-1.5">{s.name}</h4>
                <p className="text-sm text-gray-400 group-hover:text-white/60 transition-colors leading-relaxed">{s.desc}</p>
              </div>
              <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-arrow-right-line text-gray-300 group-hover:text-white transition-colors" />
              </div>
            </Link>
          ))}
        </div>

        {/* CTA block after services */}
        <div className={`bg-[#03143A] rounded-3xl p-10 flex flex-col md:flex-row items-center justify-between gap-6 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '440ms' }}>
          <div>
            <p className="text-white/60 text-sm font-bold uppercase tracking-widest mb-1">Houston businesses trust us</p>
            <h3 className="text-2xl font-bold text-white">Get a Free Facility Assessment in Houston</h3>
            <p className="text-white/55 text-sm mt-1">Fast response &bull; Custom plan &bull; Local Houston team</p>
          </div>
          <div className="flex items-center gap-4 flex-shrink-0">
            <a href="tel:+17133382553" className="flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-bold rounded-full border border-white/20 transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-phone-fill text-[#C70532]" />
              Call Now
            </a>
            <Link to="/contact" className="px-7 py-3 bg-[#C70532] hover:bg-[#a80428] text-white font-bold rounded-full transition-all cursor-pointer whitespace-nowrap">
              Get Free Quote
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── 4. Local AI Answer Block ─── */
function HoustonAIBlock() {
  const { ref, isVisible } = useScrollAnimation();
  const ANSWERS = [
    {
      icon: 'ri-map-pin-line',
      title: 'Who provides commercial cleaning in Houston?',
      body: 'Prime Facility Services Group provides professional commercial cleaning services for businesses in Houston, TX. Headquartered at 8303 Westglen Dr, Houston TX 77063, our local team delivers reliable, customized cleaning solutions for offices, retail spaces, medical facilities, and commercial buildings across all of Houston.',
    },
    {
      icon: 'ri-building-line',
      title: 'What areas of Houston do you serve?',
      body: 'We serve businesses throughout all of Houston including Downtown, Midtown, Galleria, Energy Corridor, Medical Center, The Heights, Greenway Plaza, and surrounding suburbs. Our coverage extends to Sugar Land, Katy, The Woodlands, Pearland, Pasadena, and other Greater Houston communities.',
    },
    {
      icon: 'ri-time-line',
      title: 'How quickly can cleaning service start in Houston?',
      body: 'Most Houston commercial cleaning services can begin within a few business days after our initial on-site assessment and scheduling. For urgent situations, we offer expedited onboarding and can often start within 24-48 hours of your initial contact with our local Houston team.',
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'How much does cleaning cost in Houston?',
      body: 'Houston commercial cleaning costs are based on your facility size, service frequency, and scope of work. We provide free, no-obligation assessments and custom quotes for all Houston businesses to ensure you receive maximum value and exactly the services your specific facility needs.',
    },
    {
      icon: 'ri-question-line',
      title: 'Why choose a local Houston cleaning company?',
      body: 'A local Houston cleaning partner understands the city\'s unique business environment, climate challenges, and operational demands. Prime FSG has served Houston businesses for over 15 years, giving us deep local knowledge and the ability to respond quickly to your facility\'s needs.',
    },
  ];
  const [open, setOpen] = useState<number | null>(0);

  return (
    <section className="py-24 bg-[#FAF8F5]">
      <div className="max-w-7xl mx-auto px-6">
        <div ref={ref} className={`grid lg:grid-cols-2 gap-16 items-start transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div>
            <span className="inline-block px-5 py-2 bg-[#C70532]/10 text-[#C70532] text-xs font-bold uppercase tracking-widest rounded-full mb-6">Houston Local</span>
            <h2 className="text-4xl md:text-5xl font-black text-[#03143A] mb-6 leading-tight">Who Provides Commercial Cleaning in Houston?</h2>
            <p className="text-gray-600 text-lg leading-relaxed mb-6">
              <strong className="text-[#03143A]">Prime Facility Services Group</strong> offers professional cleaning services for businesses in <strong className="text-[#03143A]">Houston, TX</strong>, delivering reliable and customized solutions for offices and commercial facilities across the entire city and surrounding areas.
            </p>
            <p className="text-gray-500 leading-relaxed mb-8">
              Our Houston headquarters at 8303 Westglen Dr means we&apos;re always close by and can respond quickly to your facility&apos;s needs — from routine scheduled cleaning to emergency situations.
            </p>
            <div className="p-6 bg-white rounded-2xl border border-gray-100 mb-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#C70532]/10 rounded-xl flex-shrink-0">
                  <i className="ri-map-pin-2-line text-[#C70532] text-xl" />
                </div>
                <div>
                  <p className="font-bold text-[#03143A]">8303 Westglen Dr, Houston, TX 77063</p>
                  <p className="text-gray-400 text-sm mt-0.5">Serving all of Greater Houston — locally owned &amp; operated</p>
                </div>
              </div>
            </div>
            <iframe
              title="Prime Facility Services Group Houston TX location map"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3464.437!2d-95.55!3d29.735!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8640c0a947b28c23%3A0x1235678900!2s8303+Westglen+Dr%2C+Houston%2C+TX+77063!5e0!3m2!1sen!2sus!4v1710000000001!5m2!1sen!2sus"
              width="100%"
              height="240"
              style={{ border: 0, borderRadius: '12px', display: 'block' }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>

          <div className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`} style={{ transitionDelay: '200ms' }}>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 flex items-center justify-center bg-[#C70532] rounded-xl">
                <i className="ri-robot-2-line text-white" />
              </div>
              <div>
                <p className="font-bold text-[#03143A] text-sm">AI Answer Block — Houston</p>
                <p className="text-gray-400 text-xs">Optimized for local search &amp; voice queries</p>
              </div>
            </div>
            <div className="space-y-3">
              {ANSWERS.map((item, i) => (
                <div key={i} className={`rounded-2xl border overflow-hidden transition-all duration-300 ${open === i ? 'border-[#C70532]/30 bg-white' : 'border-gray-200 bg-white'}`}>
                  <button className="w-full flex items-center gap-4 px-6 py-5 text-left cursor-pointer group" onClick={() => setOpen(open === i ? null : i)}>
                    <div className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 transition-colors ${open === i ? 'bg-[#C70532]' : 'bg-[#03143A]/8 group-hover:bg-[#C70532]'}`}>
                      <i className={`${item.icon} text-sm transition-colors ${open === i ? 'text-white' : 'text-[#03143A] group-hover:text-white'}`} />
                    </div>
                    <h3 className={`font-bold text-sm flex-1 pr-2 transition-colors leading-snug ${open === i ? 'text-[#C70532]' : 'text-[#03143A] group-hover:text-[#C70532]'}`}>{item.title}</h3>
                    <div className={`w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-300 ${open === i ? 'bg-[#C70532] rotate-180' : 'bg-gray-100'}`}>
                      <i className={`ri-arrow-down-s-line text-sm ${open === i ? 'text-white' : 'text-gray-500'}`} />
                    </div>
                  </button>
                  {open === i && (
                    <div className="px-6 pb-5 pl-[4.5rem]">
                      <p className="text-gray-600 text-sm leading-relaxed">{item.body}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function HoustonPage() {
  return (
    <>
      <Navbar />
      <main>
        {/* 1. Hero */}
        <HoustonHero />
        {/* 2. Trust Bar */}
        <TrustBar />
        {/* 3. Local Services (Services Overview equivalent) */}
        <LocalServices />
        {/* 4. AI Answer Block */}
        <HoustonAIBlock />
        {/* 5. Process */}
        <ProcessSection
          title="How We Start Serving Your Houston Business"
          subtitle="Getting started is simple — we come to you, assess your needs, and deliver professional results fast."
        />
        {/* 6. Why Choose Us */}
        <WhyChooseUs
          title="Why Houston Businesses Choose Prime Facility Services Group"
          subtitle="Local expertise, vetted professionals, and 15+ years of proven results that Houston businesses have depended on."
        />
        {/* 7. Service Areas */}
        <ServiceAreasSection
          title="Serving Businesses Across Houston & Surrounding Areas"
          subtitle="Prime Facility Services Group provides commercial cleaning and facility services throughout the Greater Houston area, serving all neighborhoods and suburbs."
          ctaCity="Houston"
        />
        {/* 8. Testimonials */}
        <TestimonialsSection title="What Houston Businesses Say About Us" />
        {/* 9. FAQ */}
        <FAQSection
          faqs={houstonFaqs}
          title="Houston Commercial Cleaning FAQs"
          subtitle="Local answers to the most common questions Houston businesses ask about commercial cleaning and facility services."
        />
        {/* 10. Final CTA */}
        <FinalCTASection
          title="Get a Free Quote in Houston, TX"
          subtitle="Contact our local Houston team today for a fast, customized facility services proposal — no obligation, response within 24 hours."
          ctaCity="Houston"
        />
      </main>
      <Footer />
    </>
  );
}
